import { dungApiThat, goiApi, goiApiBieuMauCoTienTrinh, goiApiTepCoTen } from '../api/http';
import { CO_TRANG_TOI_DA, docHetTrang } from '../api/phan-trang';
import { mayChuBienBan } from '../../mock/bien-ban';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  AmThanhKy, DinhDangXuat, Doan, DoanSua, Phien, PhienSua, PhienTao, TomTatGhi, TrangDoan, TrangPhien, TrangThaiPhien,
} from '../../modules/bien-ban/types';
import { giaLap, giaLapDanhSach, kiemTrang, maHoa } from './ocr-chung';

const GOC = '/api/v1/m/bien-ban/sessions';
const TRANG_RONG: TrangPhien = { items: [], next_cursor: null };

export interface LocPhien {
  search?: string;
  status?: TrangThaiPhien;
  limit: number;
  cursor?: string;
}

const goc = (id: string) => `${GOC}/${maHoa(id)}`;

/** Biên bản cuộc họp (ADR-0126 mục 5). Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả `mock/bien-ban.ts`. */
export const bienBanRepository = {
  danhSach: (loc: LocPhien): Promise<TrangPhien> =>
    dungApiThat()
      ? goiApi<TrangPhien>(GOC, { query: { ...loc } }).then(kiemTrang)
      : giaLapDanhSach(() => mayChuBienBan.danhSach(loc), TRANG_RONG),

  chiTiet: (id: string): Promise<Phien> => (dungApiThat() ? goiApi<Phien>(goc(id)) : giaLap(() => mayChuBienBan.chiTiet(id))),

  /** `POST /sessions` → `202`, multipart. Ô Tự nhận diện không gửi `language`, để máy chủ tự quyết. */
  tao: (vao: PhienTao, tuyChon: { onTienTrinh?: (phanTram: number) => void; signal?: AbortSignal }): Promise<Phien> => {
    if (!dungApiThat()) return mayChuBienBan.tao(vao, tuyChon);
    const bieuMau = new FormData();
    bieuMau.append('title', vao.title);
    if (vao.language !== 'auto') bieuMau.append('language', vao.language);
    if (vao.num_speakers !== undefined) bieuMau.append('num_speakers', String(vao.num_speakers));
    // `file` đứng cuối: bộ đọc multipart theo stream đã có đủ trường chữ khi gặp tệp.
    bieuMau.append('file', vao.file, vao.file.name);
    return goiApiBieuMauCoTienTrinh<Phien>(GOC, bieuMau, tuyChon);
  },

  sua: (id: string, vao: PhienSua): Promise<Phien> =>
    dungApiThat() ? goiApi<Phien>(goc(id), { method: 'PATCH', body: vao }) : giaLap(() => mayChuBienBan.sua(id, vao)),

  xoa: (id: string): Promise<void> =>
    dungApiThat() ? goiApi<void>(goc(id), { method: 'DELETE' }) : giaLap(() => mayChuBienBan.xoa(id)),

  /** Màn cần trọn bản chép lời để gom lượt, chọn đoạn theo mốc phát và tự cuộn. */
  doan: (id: string): Promise<Doan[]> =>
    dungApiThat()
      ? docHetTrang((cursor) => goiApi<TrangDoan>(`${goc(id)}/segments`, { query: { limit: CO_TRANG_TOI_DA, cursor } }))
      : mockState() === 'empty'
        ? mockRequest<Doan[]>([])
        : giaLap(() => mayChuBienBan.doan(id)),

  suaDoan: (id: string, doanId: string, vao: DoanSua): Promise<Doan> =>
    dungApiThat()
      ? goiApi<Doan>(`${goc(id)}/segments/${maHoa(doanId)}`, { method: 'PATCH', body: vao })
      : giaLap(() => mayChuBienBan.suaDoan(id, doanId, vao)),

  amThanh: (id: string): Promise<AmThanhKy> =>
    dungApiThat() ? goiApi<AmThanhKy>(`${goc(id)}/audio`) : giaLap(() => mayChuBienBan.amThanh(id)),

  thuLai: (id: string): Promise<void> =>
    dungApiThat() ? goiApi<void>(`${goc(id)}/retry`, { method: 'POST' }) : giaLap(() => mayChuBienBan.thuLai(id)),

  tomTatLai: (id: string): Promise<void> =>
    dungApiThat() ? goiApi<void>(`${goc(id)}/summary`, { method: 'POST' }) : giaLap(() => mayChuBienBan.tomTatLai(id)),

  suaTomTat: (id: string, vao: TomTatGhi): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`${goc(id)}/summary`, { method: 'PUT', body: vao })
      : giaLap(() => mayChuBienBan.suaTomTat(id, vao)),

  /** Tên tệp do máy chủ đặt; `tenMacDinh` chỉ dùng khi thiếu `Content-Disposition`. */
  xuat: (id: string, dinhDang: DinhDangXuat, tenMacDinh: string): Promise<{ blob: Blob; ten: string }> =>
    dungApiThat()
      ? goiApiTepCoTen(`${goc(id)}/export?format=${dinhDang}`, tenMacDinh)
      : giaLap(() => mayChuBienBan.xuat(id, dinhDang, tenMacDinh)),
};
